import React from 'react'
import { FormikContextType, FormikErrors } from 'formik/dist/types'
import { FormikProvider, useFormik } from 'formik'
import { TextInputFormik } from '../basics/TextInput/TextInput'
import { useTranslation } from 'react-i18next'
import useImageInput from '../basics/ImageInput/useImageInput'
import Button from '../basics/Button/Button'
import styles from './LearningPathForm.module.scss'
import { userCanCreateCategory } from '../../state/calculations/permissions/userCan'
import { SelectCategoriesFormik } from '../formElements/SelectCategories/SelectCategories'
import { useSession } from '../../state/session'
import { SelectFormik } from '../basics/Select/Select'
import { MarkdownEditorFormik } from '../basics/MarkdownEditor/MarkdownEditor'
import FormButtons from '../basics/FormButtons/FormButtons'
import { Category } from '../../models/Category'
import { LearningItemStatus } from '../../models/BaseModels'
import LearningPathCoursesForm from './LearningPathCoursesForm'
import { AnonymousLearningItem } from '../../models/AnonymousLearningItem'
import set from 'lodash/set'

export default React.memo(function LearningPathForm({
  initialValues,
  onSubmit,
  onClose,
}: {
  initialValues: LearningPathFormValues
  onClose: () => void
  onSubmit: (
    values: LearningPathFormValues,
    setStatus: (status?: any) => void,
    setErrors: (errors: FormikErrors<LearningPathFormValues>) => void
  ) => void
}) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()

  function validateFormValues(values: LearningPathFormValues) {
    const errors = {}

    if (!values.courses || values.courses.length === 0) {
      set(errors, 'courses', t('learning path.error add learning item'))
    }

    if (values.isAnyoneAssigned && values.status === LearningItemStatus.Draft) {
      set(errors, 'status', t('learning item.status draft error'))
    }

    return errors
  }

  const formik: EditLearningPathFormikContextType = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validate: (values) => validateFormValues(values),
    onSubmit: async (values, { setStatus, setErrors }) => onSubmit(values, setStatus, setErrors),
  })

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={styles.EditLearningPathForm}>
        <div className={styles.titleAndStatus}>
          <TextInputFormik name="title" label={t('learning path.field title')} required />
        </div>

        <div className={styles.imageAndDetailsForm}>
          <LearningPathImageForm formik={formik} />

          <div className={styles.detailsForm}>
            <div>
              <SelectFormik
                name="status"
                label={t('learning item.edit field status')}
                options={[
                  { value: 'draft', label: t('learning item.status.draft') },
                  { value: 'published', label: t('learning item.status.published') },
                  { value: 'deprecated', label: t('learning item.status.deprecated') },
                ]}
              />

              <SelectCategoriesFormik
                name="categories"
                label={t('learning path.field categories')}
                allowCreate={userCanCreateCategory(currentUser)}
              />

              <SelectFormik
                name="certificateMode"
                label={t('learning path.field certificate mode')}
                options={[
                  { value: 'none', label: t('learning path.certificate mode.none') },
                  { value: 'limited', label: t('learning path.certificate mode.limited') },
                  { value: 'unlimited', label: t('learning path.certificate mode.unlimited') },
                ]}
              />
              {formik.values.certificateMode === 'limited' && (
                <TextInputFormik
                  type="date"
                  name="certificateValidity"
                  label={t('learning path.field certificate validity')}
                  required
                />
              )}
            </div>
            <div>
              <MarkdownEditorFormik name="description" label={t('learning path.field description')} required />
            </div>
          </div>
        </div>

        <LearningPathCoursesForm formik={formik as any} />

        <FormButtons {...formik} onClose={onClose} />
      </form>
    </FormikProvider>
  )
})

export type LearningPathFormValues = {
  title: string
  description: string
  status: LearningItemStatus
  image: string | null | undefined
  certificateMode: 'none' | 'limited' | 'unlimited'
  certificateValidity: Date | null | undefined
  categories: Category[] | null
  courses: AnonymousLearningItem[] | []
  isAnyoneAssigned: boolean
}

export type EditLearningPathFormikContextType = FormikContextType<LearningPathFormValues>

/**
 * MO: I was trying to extract this component, generalize it and use
 * it in the LearningItemForm as well but hit a problem. I could
 * not write a proper common formik object type for both cases.
 */
const LearningPathImageForm = React.memo(function LearningPathImageForm({
  formik,
}: {
  formik: EditLearningPathFormikContextType
}) {
  const { t } = useTranslation()
  const setValue = (value: string | null | undefined) => formik.setFieldValue('image', value, true)

  const imageInput = useImageInput({ value: formik.values.image, setValue, initialValue: formik.initialValues.image })

  return (
    <>
      <div className={styles.LearningPathImageForm}>
        <div {...imageInput.rootProps} className={styles.image}>
          <img src={formik.values.image || undefined} />
          <input {...imageInput.inputProps} />
        </div>
        <div className={styles.actions}>
          {!imageInput.discardButtonProps.disabled && (
            <Button small {...imageInput.discardButtonProps}>
              {t('generic cta.discard')}
            </Button>
          )}
          {!imageInput.removeButtonProps.disabled && (
            <Button small {...imageInput.removeButtonProps}>
              {t('generic cta.remove')}
            </Button>
          )}
          {!imageInput.adjustButtonProps.disabled && (
            <Button small filled {...imageInput.adjustButtonProps}>
              {t('cta crop image')}
            </Button>
          )}
          <Button small filled {...imageInput.selectButtonProps}>
            {t('generic cta.upload')}
          </Button>
        </div>
      </div>
      {imageInput.children}
    </>
  )
})
